// Conversión de unidades para la presentación. Todo lo almacenado está en SI. Ver docs/theory/units.md.

export type Quantity = 'length' | 'mass' | 'speed' | 'acceleration' | 'pressure' | 'force' | 'impulse' | 'area' | 'temperature' | 'angle' | 'time';
export type UnitSystem = 'metric' | 'imperial';

export interface Unit {
  readonly id: string;
  readonly symbol: string;
  readonly quantity: Quantity;
  /** Valor en la unidad → valor en SI. */
  toSI(value: number): number;
  /** Valor en SI → valor en la unidad. */
  fromSI(value: number): number;
}

const linear = (id: string, symbol: string, quantity: Quantity, factor: number): Unit => ({
  id,
  symbol,
  quantity,
  toSI: (v) => v * factor,
  fromSI: (v) => v / factor,
});

const G0 = 9.80665;
const LBF = 4.4482216152605;

const list: readonly Unit[] = [
  linear('m', 'm', 'length', 1),
  linear('cm', 'cm', 'length', 0.01),
  linear('mm', 'mm', 'length', 0.001),
  linear('in', 'in', 'length', 0.0254),
  linear('ft', 'ft', 'length', 0.3048),
  linear('kg', 'kg', 'mass', 1),
  linear('g', 'g', 'mass', 0.001),
  linear('oz', 'oz', 'mass', 0.028349523125),
  linear('lb', 'lb', 'mass', 0.45359237),
  linear('m/s', 'm/s', 'speed', 1),
  linear('km/h', 'km/h', 'speed', 1 / 3.6),
  linear('mph', 'mph', 'speed', 0.44704),
  linear('ft/s', 'ft/s', 'speed', 0.3048),
  linear('m/s2', 'm/s²', 'acceleration', 1),
  linear('g0', 'g', 'acceleration', G0),
  linear('ft/s2', 'ft/s²', 'acceleration', 0.3048),
  linear('Pa', 'Pa', 'pressure', 1),
  linear('kPa', 'kPa', 'pressure', 1000),
  linear('hPa', 'hPa', 'pressure', 100),
  linear('atm', 'atm', 'pressure', 101325),
  linear('psi', 'psi', 'pressure', 6894.757293168),
  linear('inHg', 'inHg', 'pressure', 3386.389),
  linear('N', 'N', 'force', 1),
  linear('lbf', 'lbf', 'force', LBF),
  linear('N*s', 'N·s', 'impulse', 1),
  linear('lbf*s', 'lbf·s', 'impulse', LBF),
  linear('m2', 'm²', 'area', 1),
  linear('cm2', 'cm²', 'area', 1e-4),
  linear('in2', 'in²', 'area', 6.4516e-4),
  { id: 'K', symbol: 'K', quantity: 'temperature', toSI: (v) => v, fromSI: (v) => v },
  { id: 'C', symbol: '°C', quantity: 'temperature', toSI: (v) => v + 273.15, fromSI: (v) => v - 273.15 },
  { id: 'F', symbol: '°F', quantity: 'temperature', toSI: (v) => ((v - 32) * 5) / 9 + 273.15, fromSI: (v) => ((v - 273.15) * 9) / 5 + 32 },
  linear('rad', 'rad', 'angle', 1),
  linear('deg', '°', 'angle', Math.PI / 180),
  linear('s', 's', 'time', 1),
];

export const UNITS: ReadonlyMap<string, Unit> = new Map(list.map((u) => [u.id, u]));

export const unitsOf = (quantity: Quantity): readonly Unit[] => list.filter((u) => u.quantity === quantity);

export const SYSTEM_DEFAULTS: Readonly<Record<UnitSystem, Readonly<Record<Quantity, string>>>> = {
  metric: { length: 'm', mass: 'g', speed: 'm/s', acceleration: 'm/s2', pressure: 'hPa', force: 'N', impulse: 'N*s', area: 'cm2', temperature: 'C', angle: 'deg', time: 's' },
  imperial: { length: 'in', mass: 'oz', speed: 'ft/s', acceleration: 'ft/s2', pressure: 'inHg', force: 'lbf', impulse: 'lbf*s', area: 'in2', temperature: 'F', angle: 'deg', time: 's' },
};

export function unit(id: string): Unit {
  const u = UNITS.get(id);
  if (u === undefined) throw new RangeError(`Unidad desconocida: ${id}`);
  return u;
}

/** Convierte entre dos unidades de la misma magnitud. */
export function convert(value: number, from: string, to: string): number {
  const a = unit(from);
  const b = unit(to);
  if (a.quantity !== b.quantity) throw new RangeError(`No se puede convertir ${a.quantity} a ${b.quantity}`);
  return b.fromSI(a.toSI(value));
}

export interface Displayed {
  readonly value: number;
  readonly symbol: string;
  readonly unitId: string;
}

/** Valor en SI → valor y símbolo en la unidad que el sistema de unidades elige para la magnitud. */
export function toDisplay(valueSI: number, quantity: Quantity, system: UnitSystem, unitId?: string): Displayed {
  const u = unit(unitId ?? SYSTEM_DEFAULTS[system][quantity]);
  if (u.quantity !== quantity) throw new RangeError(`La unidad ${u.id} no es de ${quantity}`);
  return { value: u.fromSI(valueSI), symbol: u.symbol, unitId: u.id };
}

/** Texto en la unidad elegida, con `digits` cifras significativas. */
export function formatQuantity(valueSI: number, quantity: Quantity, system: UnitSystem, digits = 4, unitId?: string): string {
  const d = toDisplay(valueSI, quantity, system, unitId);
  if (!Number.isFinite(d.value)) return `— ${d.symbol}`;
  return `${Number(d.value.toPrecision(digits))} ${d.symbol}`;
}

/** Lo que escribe el usuario en la unidad `unitId` → SI. */
export const fromDisplay = (value: number, unitId: string): number => unit(unitId).toSI(value);
